import clsx from 'clsx';
import * as React from 'react';

import type * as api from '@gitbook/api-client';
import { type InlineMentionData, selectInline } from '@gitbook/doc-core';
import type { InlineProps } from '@gitbook/slate-react';
import { type IconDefinition, renderIcon } from '@gitbook/spine-react';

import {
    type UIResolvedContentReference,
    useContentReference,
    useContentRefNavigationProps,
} from '../../ContentReferences';
import {
    InlineContentRefEditPopover,
    InlineContentRefPreviewPopover,
    useIsInlineActive,
} from '../inlines';
import { ALLOWED_CONTENT_REFS_IN_INLINE_MENTION } from './util';

/**
 * Component to render an inline mention.
 */
export function InlineMention(props: InlineProps): React.ReactElement {
    return (
        <React.Suspense fallback={<MentionSpan label="Loading…" {...props.attributes} aria-busy />}>
            <ResolvedInlineMention {...props} />
        </React.Suspense>
    );
}

/**
 * Resolves an inline mention, it suspenses.
 */
function ResolvedInlineMention(props: InlineProps): React.ReactElement {
    const { node, attributes, editor, isSelected } = props;

    const data = node.data.toFrozenObject() as InlineMentionData;
    const resolved = useContentReference(data.ref);
    const [isActive, inlineProps] = useIsInlineActive(props);
    const mention = (
        <ContentRefMention
            readOnly={editor.readOnly}
            resolved={resolved}
            contentRef={data.ref}
            onClick={() => {
                editor.change((change) => {
                    selectInline(change, node);
                });
            }}
            isActive={isSelected}
            {...attributes}
            {...inlineProps}
        />
    );

    if (isActive) {
        if (editor.readOnly) {
            return (
                <InlineContentRefPreviewPopover
                    label="Mention"
                    resolved={resolved}
                    contentRef={data.ref}
                >
                    {mention}
                </InlineContentRefPreviewPopover>
            );
        }
        return (
            <InlineContentRefEditPopover
                label="Mention"
                resolved={resolved}
                contentRef={data.ref}
                accept={ALLOWED_CONTENT_REFS_IN_INLINE_MENTION}
                {...props}
            >
                {mention}
            </InlineContentRefEditPopover>
        );
    }

    return mention;
}

/**
 * Render a mention with a resolved content ref.
 */
const ContentRefMention = React.forwardRef(function ContentRefMention(
    props: React.HTMLAttributes<HTMLElement> &
        Pick<ContentRefLinkMentionProps, 'contentRef' | 'readOnly' | 'isActive'> &
        Pick<MentionSpanProps, 'isActive'> & {
            resolved: UIResolvedContentReference;
        },
    ref: React.ForwardedRef<HTMLElement>
) {
    const { contentRef, resolved, readOnly, ...rest } = props;
    switch (contentRef.kind) {
        case 'user': {
            return (
                <MentionSpan
                    ref={ref}
                    icon={resolved.icon || resolved.secondaryIcon}
                    label={`@${resolved.title}`}
                    {...rest}
                />
            );
        }
        default: {
            return (
                <ContentRefLinkMention
                    ref={ref as React.ForwardedRef<HTMLAnchorElement>}
                    {...props}
                />
            );
        }
    }
});

type ContentRefLinkMentionProps = Omit<MentionAnchorProps, 'label' | 'icon'> & {
    contentRef: api.ContentRef;
    readOnly: boolean;
    resolved: UIResolvedContentReference;
};

/**
 * Render a mention that is a link.
 */
const ContentRefLinkMention = React.forwardRef(function ContentRefLinkMention(
    props: ContentRefLinkMentionProps,
    ref: React.ForwardedRef<HTMLAnchorElement>
) {
    const { contentRef, resolved, readOnly, ...rest } = props;
    const navProps = useContentRefNavigationProps(readOnly ? resolved : null, props);
    return (
        <MentionAnchor
            ref={ref}
            {...rest}
            {...navProps}
            icon={
                contentRef.kind === 'space' || contentRef.kind === 'page'
                    ? resolved.icon || resolved.secondaryIcon
                    : resolved.secondaryIcon
            }
            label={resolved.title}
        />
    );
});

type MentionProps = {
    className?: string;
    icon?: IconDefinition;
    label: string;
    isActive?: boolean;
};

/**
 * Common hook to render low-level mention tag.
 */
function useMentionProps<T extends object>(props: MentionProps & T) {
    const { className, icon, label, isActive, ...rest } = props;
    return {
        className: clsx(
            'select-none whitespace-nowrap rounded bg-[var(--sp-color-bg-mention-default)] px-1 pt-[1px] pb-[0.5px] text-primary no-underline',
            'href' in rest && rest.href ? 'cursor-pointer' : 'cursor-default',
            isActive ? 'bg-active' : 'bg-[var(--sp-color-bg-mention-default)]',
            className
        ),
        'data-mention': '',
        children: (
            <>
                {icon ? (
                    <span className="-top-[1px] relative mr-[0.25em] inline-flex align-middle">
                        {renderIcon(icon)}
                    </span>
                ) : null}
                <span className="whitespace-pre-wrap">{label}</span>
            </>
        ),
        ...rest,
    };
}

type MentionSpanProps = React.ComponentPropsWithoutRef<'span'> & MentionProps;

/**
 * Low-level mention span.
 */
const MentionSpan = React.forwardRef(function MentionSpan(
    props: MentionSpanProps,
    ref: React.ForwardedRef<HTMLSpanElement>
) {
    const renderProps = useMentionProps(props);
    return <span ref={ref} {...renderProps} />;
});

type MentionAnchorProps = React.ComponentPropsWithoutRef<'a'> & MentionProps;

/**
 * Low-level mention anchor.
 */
const MentionAnchor = React.forwardRef(function MentionAnchor(
    props: MentionAnchorProps,
    ref: React.ForwardedRef<HTMLAnchorElement>
) {
    const renderProps = useMentionProps(props);
    return <a ref={ref} {...renderProps} />;
});
